import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  projectsApi, usersApi, weeklyReportApi, type InternalWeeklyReport, type WeeklyReportDay, type WeeklyReportSectionKey,
} from '@/lib/apiClient';
import { Badge, Button, Modal, StateView } from '@/components';
import { useGraphToken, useThumbnailCache } from '@/lib/graphHooks';
import { projectStage } from '@/lib/projectStage';
import { easternTodayYmd, getWeekSunday, localYmd } from '@/modules/manpower/lib/gridDates';
import {
  addYmd, crewTotals, customerTeamRows, dayHasExportContent, dayHeading, daysByWeek, detailRows, exportPhotoUrls,
  extraSectionsAfter, longDate, lookaheadLines, MANPOWER_FOOTNOTE, manpowerRows, photoCount, quantityLine, rangeLabel, reportFilename, sectionLines,
  sectionTables, stripWeeks, teamWithCarry, theWeekOf, typedEquipment, weatherLine, weekOf, type CarriedSection,
} from './weeklyReportModel';
import { buildClientReportDocx, buildInternalReportDocx, CUSTOMER_TEAM_ROLES, downloadDocx } from './weeklyReportDocx';
import { loadLogo, loadReportPhotos } from './reportPhotos';
import WeekStrip, { type WeekMark } from './WeekStrip';
import FinalizedReports from './FinalizedReports';
import FiledReport, { Blocks, ReportTable } from './FiledReport';
import ReportPhotoGrid from './ReportPhotoGrid';

/**
 * The Weekly Report tab (#576 internal, #579 client).
 *
 * One report week at a time, picked on a strip of week cells like the crew
 * schedule's header. The tab is laid out like the Word report itself, section
 * for section and in the same order, set like the template (dark titles, green
 * table headers), so what
 * the PM sees here is what the export will hold:
 *   • the parts the Tracker fills in (project details, the team, the week
 *     before's daily logs with photos, manpower, equipment), and
 *   • the narrative sections, marked as written in Word after export, or
 *     showing what last week's finalized report said, which the export
 *     starts from.
 * The PM reviews the file before sending it, then uploads the finished file
 * to its week (FinalizedReports.tsx). The uploads are the only thing stored.
 *
 * A toggle right above the report (#576 follow-up, product owner 2026-10-12)
 * switches each week between:
 *   • BEFORE UPLOAD: the draft above, what the export will hold; and
 *   • AFTER UPLOAD: the finalized internal report as filed, its whole text
 *     read from the uploaded Word file (FiledReport.tsx), so nobody has to
 *     open the file in SharePoint to read it.
 * A week with a filed report opens on it; the choice then holds week to week.
 *
 * Two exports:
 *   • Internal: every submitted daily log from the week before, with photos.
 *   • Client: opens a window first. No day is ticked, so a client gets no
 *     daily log unless the PM picks it. The server builds the client report
 *     from its allowlist; Manpower and Equipment never reach it.
 */

const css = `
  .wr-top { display: flex; align-items: center; gap: var(--ew-s-3); margin-bottom: var(--ew-s-2); flex-wrap: wrap; }
  .wr-range { font-size: 15px; font-weight: 700; color: var(--ew-dark); }
  .wr-hint { font-size: 12px; color: var(--ew-n-500); }
  .wr-actions { margin-left: auto; display: inline-flex; gap: var(--ew-s-2); flex-wrap: wrap; }
  .wr-gap { height: var(--ew-s-4); }
  .wr-notice { margin-bottom: var(--ew-s-4); padding: 10px 14px; border-radius: var(--ew-r-md); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .wr-notice-info { background: var(--ew-n-100); color: var(--ew-dark); }
  .wr-notice-err { background: #FBE4DF; color: #8F2414; }

  /* The report on one white page, set like the template: section titles in
     dark bold capitals with no fill. The template's only green is its table
     header rows, so that is the only green here too. */
  .wr-doc { background: var(--ew-white); border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 20px 24px 8px; }
  .wr-sec { margin-bottom: 22px; }
  .wr-sec-hd {
    margin: 0 0 8px; display: flex; align-items: center; gap: var(--ew-s-2);
    color: var(--ew-dark); font: 700 14px/1.2 var(--ew-font); letter-spacing: 0.04em; text-transform: uppercase;
  }
  .wr-tag { padding: 2px 8px; border-radius: 999px; background: var(--ew-n-100); color: var(--ew-n-700); font-size: 10px; letter-spacing: 0.08em; }
  .wr-sec-body { font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.45 var(--ew-font); color: var(--ew-dark); }
  .wr-sec-body > :first-child { margin-top: 0; }
  .wr-sub { margin: var(--ew-s-3) 0 6px; color: var(--ew-dark); font: 700 13px/1.2 var(--ew-font); }
  .wr-sub:first-child { margin-top: 0; }
  .wr-word {
    padding: 8px 12px; border: 1px dashed var(--ew-n-200); border-radius: var(--ew-r-sm);
    color: var(--ew-n-500); font-style: italic; background: var(--ew-n-50, #FAFAF9);
  }
  /* Text carried from last week's finalized report: a draft, edited in Word. */
  .wr-carry { padding: 8px 12px; border: 1px dashed var(--ew-n-200); border-radius: var(--ew-r-sm); background: var(--ew-n-50, #FAFAF9); }
  .wr-carry ul { margin: 0; padding-left: 18px; white-space: pre-line; }
  .wr-carry-src { margin-top: 4px; font-size: 11px; color: var(--ew-n-500); font-style: italic; }
  .wr-carried { color: var(--ew-n-500); font-size: 11px; font-style: italic; }

  /* Tables, like the report's: green header row, light grid. */
  .wr-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .wr-table th {
    text-align: left; padding: 7px 10px; white-space: nowrap;
    background: var(--ew-green); color: var(--ew-white); font: 700 12px/1.2 var(--ew-font);
    border: 1px solid var(--ew-green);
  }
  .wr-table td { padding: 7px 10px; border: 1px solid var(--ew-n-200); color: var(--ew-dark); vertical-align: top; }
  .wr-table td.wr-label { font-weight: 600; width: 30%; }
  .wr-muted { color: var(--ew-n-500); }
  .wr-fill { color: var(--ew-n-500); font-style: italic; }
  .wr-list { margin: 0; padding-left: 18px; }

  .wr-week-logs { margin-top: var(--ew-s-3); }
  .wr-day { margin-bottom: var(--ew-s-4); }
  .wr-day-hd {
    display: flex; align-items: center; gap: var(--ew-s-2); flex-wrap: wrap;
    padding: 4px 0 5px; border-bottom: 1px solid var(--ew-n-200); color: var(--ew-dark); font: 700 13px/1.2 var(--ew-font);
  }
  .wr-day-hd a, .wr-day-hd a:visited { margin-left: auto; font-size: 12px; color: var(--ew-green-700); text-decoration: none; }
  .wr-day-hd a:hover { text-decoration: underline; }
  .wr-day-body { padding: 8px 0 0; }
  .wr-day-body p { margin: 4px 0 0; }
  .wr-day-body p:first-child { margin-top: 0; }
  .wr-day-body ul { margin: 4px 0 0; padding-left: 18px; }
  .wr-day-label { margin-top: 8px; font-weight: 700; }
  .wr-photos { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 10px; margin-top: 10px; }
  .wr-photo-fig { margin: 0; }
  .wr-photo { width: 100%; aspect-ratio: 4 / 3; border-radius: var(--ew-r-sm); background: var(--ew-n-100); object-fit: cover; display: block; }
  .wr-photo-ph { width: 100%; aspect-ratio: 4 / 3; border-radius: var(--ew-r-sm); background: var(--ew-n-100); display: flex; align-items: center; justify-content: center; font-size: 11px; color: var(--ew-n-500); text-align: center; padding: 6px; box-sizing: border-box; overflow: hidden; }
  .wr-photo-cap { margin-top: 4px; font-size: 11px; color: var(--ew-n-500); text-align: center; }

  /* Before upload | After upload, right above the report. */
  .wr-views {
    display: inline-flex; gap: 2px; padding: 3px; margin-bottom: var(--ew-s-3);
    background: var(--ew-n-100); border-radius: var(--ew-r-lg);
  }
  .wr-view {
    border: 0; background: transparent; cursor: pointer; border-radius: var(--ew-r-md);
    padding: 8px 16px; min-height: 36px; color: var(--ew-n-700);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
  }
  .wr-view[aria-pressed="true"] { background: #fff; color: var(--ew-dark); box-shadow: var(--ew-shadow-1); }
  .wr-view:disabled { cursor: not-allowed; opacity: 0.55; }
  .wr-view-bar { display: flex; align-items: center; gap: var(--ew-s-3); flex-wrap: wrap; }
  .wr-view-bar .wr-hint { margin-bottom: var(--ew-s-3); }
  @media (max-width: 600px) { .wr-views { display: flex; width: 100%; } .wr-view { flex: 1; } }
  /* The filed report's own lines keep their line breaks. */
  .wr-filed p, .wr-filed li { white-space: pre-line; }
  .wr-filed p { margin: 4px 0; }
  .wr-filed .wr-day-hd { margin-top: var(--ew-s-3); }

  .wr-pick-week { margin: var(--ew-s-3) 0 var(--ew-s-1); display: flex; align-items: center; justify-content: space-between; font-weight: var(--ew-fw-semibold); }
  .wr-pick { display: flex; align-items: flex-start; gap: 8px; padding: 6px 0; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .wr-pick input { margin-top: 3px; }
`;

function useCanEditProject(): boolean {
  const me = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 }).data;
  return !!me && (me.roles.includes('Admin') || me.permissions.includes('projects.edit'));
}

/**
 * The Sunday of the week a date falls in, by the crew schedule's own helpers,
 * so the tab's week and the schedule's weekly column are the same seven days.
 */
function sundayOf(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return localYmd(getWeekSunday(new Date(y, m - 1, d)));
}

const isYmd = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

type ExportState =
  | { phase: 'idle' }
  | { phase: 'working'; kind: 'internal' | 'client'; step: string }
  | { phase: 'done'; message: string }
  | { phase: 'error'; message: string };

export default function WeeklyReportTab({ projectId }: { projectId: number }) {
  const [params, setParams] = useSearchParams();
  const thisWeek = sundayOf(easternTodayYmd());
  const urlWeek = params.get('week');
  const weekStart = isYmd(urlWeek) ? sundayOf(urlWeek) : thisWeek;

  const [exportState, setExportState] = useState<ExportState>({ phase: 'idle' });
  /**
   * The week the client-days window was opened for, or null when closed.
   * ⚠️ A week, not a yes/no: the page's week can change under an open window
   * (Saturday midnight with no ?week= in the link, or Back), and the window
   * must not then export one week's ticked days against another week.
   */
  const [pickingWeek, setPickingWeek] = useState<string | null>(null);
  // Closed, not just hidden, once the page leaves its week: otherwise coming
  // back to that week would pop the window open again with nobody asking.
  if (pickingWeek && pickingWeek !== weekStart) setPickingWeek(null);
  const busy = exportState.phase === 'working';
  /**
   * Before or after upload, as last picked; null until someone picks, when a
   * week with a filed report opens on it. Kept across weeks, and shown only
   * when the week has a filed report to show.
   */
  const [view, setView] = useState<'before' | 'after' | null>(null);

  // Merge rather than replace, so ?tab=weekly survives; the current week is
  // the default and leaves no param, like the Manpower schedule's ?start=.
  const setWeek = (ws: string) => {
    // A finished export's message belongs to the week it was for.
    if (exportState.phase === 'done' || exportState.phase === 'error') setExportState({ phase: 'idle' });
    const next = new URLSearchParams(params);
    next.set('tab', 'weekly');
    if (ws === thisWeek) next.delete('week'); else next.set('week', ws);
    setParams(next, { replace: true });
  };

  // The previous week stays on screen while the next one loads, like the
  // Manpower schedule. ⚠️ So the export buttons wait for it: the client
  // window lists THIS data's days, and offering last week's days for this
  // week's export would be refused by the server as outside the lookback.
  const query = useQuery({
    queryKey: ['weekly-report', projectId, weekStart],
    queryFn: () => weeklyReportApi.internal(projectId, weekStart),
    placeholderData: (prev) => prev,
  });
  const canEdit = useCanEditProject();

  // The job's folder (uploads go there) and its stage (no filing before a Sage
  // number). The same query and key the workspace already holds.
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string | null; jobFolderUrl: string | null; bidClosedWonAt: string | null }>,
    staleTime: 60_000,
  }).data;
  const locked = project ? projectStage(project.jobNumber, project.bidClosedWonAt).activityLocked : false;

  // Each week's finalized uploads across the strip's span, for its flags and the panel.
  const span = useMemo(() => {
    const weeks = stripWeeks(thisWeek, weekStart);
    return { from: weeks[0], to: weeks[weeks.length - 1] };
  }, [thisWeek, weekStart]);
  const uploads = useQuery({
    queryKey: ['weekly-uploads', projectId, span.from, span.to],
    queryFn: () => weeklyReportApi.uploads(projectId, span.from, span.to),
    placeholderData: (prev) => prev,
  });
  const marks = useMemo(() => {
    const out: Record<string, WeekMark> = {};
    for (const w of uploads.data?.weeks ?? []) {
      // No flag on a job that can't file yet (no Sage number): nobody could clear it.
      if (w.flagged && !locked) out[w.weekStart] = 'flag';
      else if (w.done) out[w.weekStart] = 'done';
    }
    return out;
  }, [uploads.data, locked]);
  const uploadWeek = uploads.data?.weeks.find((w) => w.weekStart === weekStart);

  const graph = useGraphToken();
  const report = query.data;
  const photoUrls = useMemo(() => (report ? exportPhotoUrls(report) : []), [report]);
  const { thumbs, settled, reset } = useThumbnailCache(photoUrls, graph.silent);

  /**
   * Fetch the report fresh, load its photos, build and download the file.
   *
   * The SharePoint token is taken FIRST, inside the click, because only a
   * tap may open the sign-in popup and the later awaits would no longer count
   * as one.
   */
  const runExport = async (kind: 'internal' | 'client', days: string[] = [], week: string = weekStart) => {
    if (busy) return;
    // Busy BEFORE the sign-in await, so the buttons are off while the popup is
    // open; otherwise a second click starts a second export and download.
    setExportState({ phase: 'working', kind, step: 'Signing in to SharePoint for the photos…' });
    let token: string | null = null;
    try { token = await graph.interactive(); } catch { token = null; }
    const getToken = async () => {
      if (token) return token;
      throw new Error('No SharePoint access');
    };
    try {
      setExportState({ phase: 'working', kind, step: 'Reading the report…' });
      // The week the export was asked for, not whatever the page shows by now.
      const fresh = kind === 'internal'
        ? await weeklyReportApi.internal(projectId, week)
        : await weeklyReportApi.client(projectId, week, days);
      const urls = exportPhotoUrls(fresh);
      const photos = token && urls.length
        ? await loadReportPhotos(urls, getToken, (done, total) =>
          setExportState({ phase: 'working', kind, step: `Loading photos ${done} of ${total}…` }))
        : new Map();
      setExportState({ phase: 'working', kind, step: 'Building the document…' });
      const logo = await loadLogo();
      const doc = fresh.version === 'internal'
        ? buildInternalReportDocx(fresh, photos, logo)
        : buildClientReportDocx(fresh, photos, logo);
      await downloadDocx(doc, reportFilename(fresh));
      const missing = urls.length - photos.size;
      const label = kind === 'internal' ? 'Internal' : 'Client';
      setExportState({
        phase: 'done',
        message: missing > 0
          ? `${label} report downloaded. ${missing} of ${urls.length} photo${urls.length === 1 ? '' : 's'} couldn't be loaded${token ? '' : ' (SharePoint sign-in was declined)'}; each is marked "Photo not available" in the document.`
          : `${label} report downloaded. Review it in Word and fill in the narrative sections before sending.`,
      });
    } catch (e) {
      setExportState({ phase: 'error', message: `Couldn't export the report: ${e instanceof Error ? e.message : 'unknown error'}` });
    }
  };

  const exportDisabled = !report || busy || query.isPlaceholderData;

  return (
    <>
      <style>{css}</style>

      <div className="wr-top">
        <span className="wr-range">Report week · {rangeLabel(weekStart, addYmd(weekStart, 6))}</span>
        <span className="wr-hint">Daily logs from the week before</span>
        {query.isFetching && !query.isLoading && <span className="wr-hint" style={{ color: 'var(--ew-quality)', fontWeight: 600 }}>updating…</span>}
        {weekStart !== thisWeek && (
          <button type="button" className="ew-btn-link" onClick={() => setWeek(thisWeek)}>Back to this week</button>
        )}
        {canEdit && (
          <span className="wr-actions">
            <Button size="sm" variant="secondary" disabled={exportDisabled} isLoading={exportState.phase === 'working' && exportState.kind === 'internal'} onClick={() => void runExport('internal')}>
              Export internal (Word)
            </Button>
            <Button size="sm" disabled={exportDisabled} isLoading={exportState.phase === 'working' && exportState.kind === 'client'} onClick={() => setPickingWeek(weekStart)}>
              Export client (Word)
            </Button>
          </span>
        )}
      </div>

      <WeekStrip selected={weekStart} thisWeek={thisWeek} onSelect={setWeek} marks={marks} />
      <div className="wr-gap" />

      {exportState.phase === 'working' && <div className="wr-notice wr-notice-info" role="status">{exportState.step}</div>}
      {exportState.phase === 'done' && <div className="wr-notice wr-notice-info" role="status">{exportState.message}</div>}
      {exportState.phase === 'error' && <div className="wr-notice wr-notice-err" role="alert">{exportState.message}</div>}

      {/* Said, not hidden: without it the tab would look like a job with nothing owed. */}
      {uploads.isError && !uploads.data && (
        <div className="wr-notice wr-notice-err" role="alert">
          Couldn&rsquo;t load the finalized reports, so weeks still owed aren&rsquo;t flagged: {uploads.error instanceof Error ? uploads.error.message : 'unknown error'}
        </div>
      )}
      {/* Only once both match the selected week, so a panel never offers last week's state for this week. */}
      {report && !query.isPlaceholderData && uploads.data && (
        <FinalizedReports
          key={weekStart}
          projectId={projectId}
          report={report}
          week={uploadWeek}
          chainStart={uploads.data.chainStart}
          thisWeek={uploads.data.thisWeek}
          available={uploads.data.available}
          canEdit={canEdit}
          locked={locked}
          jobFolderUrl={project?.jobFolderUrl ?? null}
          getToken={graph.interactive}
          onGoToWeek={setWeek}
        />
      )}

      <StateView query={query} isEmpty={() => false}>
        {(r) => {
          const shown = r.filed && (view ?? 'after') === 'after' ? 'after' : 'before';
          const signIn = async () => { try { await graph.interactive(); reset(); } catch { /* stays signed out */ } };
          return (
            <>
              <div className="wr-view-bar">
                <div className="wr-views" role="group" aria-label="Which version of the report">
                  <button type="button" className="wr-view" aria-pressed={shown === 'before'} onClick={() => setView('before')}>
                    Before upload
                  </button>
                  <button type="button" className="wr-view" aria-pressed={shown === 'after'} disabled={!r.filed} onClick={() => setView('after')}>
                    After upload
                  </button>
                </div>
                <span className="wr-hint">
                  {shown === 'after'
                    ? 'The finalized internal report, as uploaded.'
                    : r.filed
                      ? 'The Tracker’s draft for this week, as the export starts it.'
                      : 'The Tracker’s draft for this week. Once the finalized internal report is uploaded, it shows under After upload.'}
                </span>
              </div>
              {shown === 'after' && r.filed ? (
                <FiledReport
                  // Its own state (a held re-read, an error) belongs to this week's
                  // upload: kept across a week change, a held read would be stored
                  // over another week's report.
                  key={`${r.window.weekStart}-${r.filed.uploadId}`}
                  projectId={projectId}
                  report={r}
                  filed={r.filed}
                  thumbs={thumbs}
                  settled={settled}
                  needsSignIn={graph.needsSignIn}
                  onSignIn={signIn}
                  canEdit={canEdit}
                  locked={locked}
                  // Undefined while the job loads: nothing to offer or explain yet.
                  jobFolderUrl={project ? project.jobFolderUrl : undefined}
                  getToken={async () => {
                    const signedOut = graph.needsSignIn;
                    const token = await graph.interactive();
                    // Signed in by the re-read's popup: the photos can load now too.
                    if (signedOut) reset();
                    return token;
                  }}
                />
              ) : (
                <ReportBody report={r} thumbs={thumbs} settled={settled} needsSignIn={graph.needsSignIn} onSignIn={signIn} />
              )}
            </>
          );
        }}
      </StateView>

      {/* Only while its own week is the one on screen, with that week's data. */}
      {pickingWeek && pickingWeek === weekStart && report && !query.isPlaceholderData && report.window.weekStart === pickingWeek && (
        <ClientDaysModal
          report={report}
          onClose={() => setPickingWeek(null)}
          onExport={(days) => { setPickingWeek(null); void runExport('client', days, report.window.weekStart); }}
        />
      )}
    </>
  );
}

// ─── The report, section for section ─────────────────────────────────────────

function Section({ title, tag, children }: { title: string; tag?: string; children: ReactNode }) {
  return (
    <section className="wr-sec">
      <h2 className="wr-sec-hd">{title}{tag && <span className="wr-tag">{tag}</span>}</h2>
      <div className="wr-sec-body">{children}</div>
    </section>
  );
}

/**
 * A narrative section's body: what the PM writes in Word after export, or,
 * once a previous finalized report has been uploaded, what it said, which the
 * export starts from.
 */
function WrittenInWord({ lines = [], fromWeek }: { lines?: string[]; fromWeek?: string }) {
  if (!lines.length || !fromWeek) return <div className="wr-word">Written in Word after export</div>;
  return (
    <div className="wr-carry">
      <ul>{lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
      <div className="wr-carry-src">From {theWeekOf(fromWeek)}&rsquo;s finalized report · edit in Word after export</div>
    </div>
  );
}

function CarriedNarrative({ title, report, section }: { title: string; report: InternalWeeklyReport; section: CarriedSection }) {
  const carry = report.carryForward;
  const tables = sectionTables(carry, section);
  return (
    <Section title={title}>
      <WrittenInWord lines={sectionLines(carry, section)} fromWeek={carry?.fromWeek} />
      {tables.map((tb, i) => <ReportTable key={i} rows={tb.rows} header={tb.header} />)}
    </Section>
  );
}

/**
 * Sections the PM added last week that came right after `after`, carried
 * whole into this draft. Text that was above Project Details has no title,
 * and none is invented for it: the export writes it with no heading either.
 */
function ExtraSections({ report, after }: { report: InternalWeeklyReport; after: WeeklyReportSectionKey | null }) {
  const carry = report.carryForward;
  if (!carry) return null;
  return (
    <>
      {extraSectionsAfter(carry, after).map((s, i) => {
        const body = (
          <div className="wr-carry wr-filed">
            {s.blocks.length ? <Blocks blocks={s.blocks} /> : <p style={{ margin: 0 }} className="wr-muted">Left empty last week.</p>}
            <div className="wr-carry-src">From {theWeekOf(carry.fromWeek)}&rsquo;s finalized report · edit in Word after export</div>
          </div>
        );
        return s.title
          ? <Section key={i} title={s.title} tag="Added in Word">{body}</Section>
          : <section key={i} className="wr-sec"><div className="wr-sec-body">{body}</div></section>;
      })}
    </>
  );
}

function ReportBody({ report, thumbs, settled, needsSignIn, onSignIn }: {
  report: InternalWeeklyReport;
  thumbs: Record<string, string>;
  settled: (u: string) => boolean;
  needsSignIn: boolean;
  onSignIn: () => void;
}) {
  const p = report.project;
  const w = report.window;
  const customer = p.client?.trim();
  const carry = report.carryForward;
  const customerRows = customerTeamRows(carry);
  return (
    <div className="wr-doc">
      {carry && (
        <div className="wr-notice wr-notice-info">
          Fields the Tracker doesn&rsquo;t store are filled from {theWeekOf(carry.fromWeek)}&rsquo;s finalized internal report,
          as a starting point. Edit them in Word after export.
        </div>
      )}
      <ExtraSections report={report} after={null} />
      <Section title="Project Details">
        <div className="ew-table-scroll">
          <table className="wr-table">
            <thead><tr><th>Field</th><th>Value</th></tr></thead>
            <tbody>
              {detailRows(p, carry).map((d, i) => (
                // By position: a row the PM added can repeat another's label.
                <tr key={i}>
                  <td className="wr-label">{d.label}</td>
                  <td>
                    {d.value || '—'}
                    {d.carried && carry && <div className="wr-carried">from {theWeekOf(carry.fromWeek)}&rsquo;s report</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <ExtraSections report={report} after="details" />

      <Section title={customer ? `${customer} Team` : 'Customer Team'}>
        <div className="ew-table-scroll">
          <table className="wr-table ew-cards">
            <thead><tr><th>Role</th><th>Name</th><th>Phone</th><th>Email</th></tr></thead>
            <tbody>
              {customerRows
                ? customerRows.map((c, i) => (
                  <tr key={`${c.role}-${i}`}>
                    <td data-label="Role" className="wr-label">{c.role || '—'}</td>
                    <td data-label="Name">{c.name ?? '—'}</td>
                    <td data-label="Phone">{c.phone ?? '—'}</td>
                    <td data-label="Email">{c.email ?? '—'}</td>
                  </tr>
                ))
                : CUSTOMER_TEAM_ROLES.map((role) => (
                  <tr key={role}>
                    <td data-label="Role" className="wr-label">{role}</td>
                    <td data-label="Name" colSpan={3} className="wr-fill">Filled in Word; the Tracker doesn&rsquo;t store customer contacts</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Section>
      <ExtraSections report={report} after="customerTeam" />

      <Section title="East & Westbrook Construction Team">
        <div className="ew-table-scroll">
          <table className="wr-table ew-cards">
            <thead><tr><th>Role</th><th>Name</th><th>Phone</th><th>Email</th></tr></thead>
            <tbody>
              {teamWithCarry(report.team, carry).map((t, i) => (
                <tr key={`${t.role}-${i}`}>
                  <td data-label="Role" className="wr-label">{t.role}</td>
                  {t.name
                    ? (
                      <>
                        <td data-label="Name">{t.name}{t.carried && carry && <div className="wr-carried">from {theWeekOf(carry.fromWeek)}&rsquo;s report</div>}</td>
                        <td data-label="Phone">
                          {t.phone ?? '—'}
                          {t.carriedContact?.includes('phone') && carry && <div className="wr-carried">from {theWeekOf(carry.fromWeek)}&rsquo;s report</div>}
                        </td>
                        <td data-label="Email">
                          {t.email ?? '—'}
                          {t.carriedContact?.includes('email') && carry && <div className="wr-carried">from {theWeekOf(carry.fromWeek)}&rsquo;s report</div>}
                        </td>
                      </>
                    )
                    : <td data-label="Name" colSpan={3} className="wr-fill">Not assigned on this job; fill in Word</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <ExtraSections report={report} after="ewTeam" />

      {/* Each lookback week: the PM's summary (written in Word), then that
          week's daily logs by date, as the export lays them out (#576). */}
      <Section title="Weekly Progress Summary">
        {needsSignIn && (
          <div className="wr-notice wr-notice-info" style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', flexWrap: 'wrap' }}>
            Sign in to SharePoint to see the photos here and include them in the export.
            <Button size="sm" variant="secondary" onClick={onSignIn}>Show photos</Button>
          </div>
        )}
        {daysByWeek(report).map(({ weekStart, days }) => (
          <div key={weekStart}>
            <h3 className="wr-sub">{weekOf(weekStart)}</h3>
            <div className="wr-word">Summary written in Word after export</div>
            <div className="wr-week-logs">
              {days.length === 0
                ? <p className="wr-muted" style={{ margin: 0 }}>No daily logs or photos this week.</p>
                : days.map((d) => <DayView key={d.date} day={d} thumbs={thumbs} settled={settled} />)}
            </div>
          </div>
        ))}
      </Section>
      <ExtraSections report={report} after="progress" />
      {/* By date, so last report's 2nd and 3rd weeks land on this one's 1st and 2nd. */}
      <Section title="Three-Week Look Ahead">
        {w.lookahead.map((ws) => (
          <div key={ws}>
            <h3 className="wr-sub">{weekOf(ws)}</h3>
            <WrittenInWord lines={lookaheadLines(carry, ws)} fromWeek={carry?.fromWeek} />
          </div>
        ))}
      </Section>
      <ExtraSections report={report} after="lookahead" />
      <CarriedNarrative title="Materials" report={report} section="materials" />
      <ExtraSections report={report} after="materials" />
      <ManpowerSection report={report} />
      <ExtraSections report={report} after="manpower" />
      <CarriedNarrative title="Survey" report={report} section="survey" />
      <ExtraSections report={report} after="survey" />
      <CarriedNarrative title="Safety" report={report} section="safety" />
      <ExtraSections report={report} after="safety" />
      <EquipmentSection report={report} />
      <ExtraSections report={report} after="equipment" />
      <CarriedNarrative title="Logistics" report={report} section="logistics" />
      <ExtraSections report={report} after="logistics" />
    </div>
  );
}

/** Manpower as the export writes it: the Tracker's counts, last week's notes on the same weeks, and rows the PM added. */
function ManpowerSection({ report }: { report: InternalWeeklyReport }) {
  const carry = report.carryForward;
  const from = carry ? <div className="wr-carried">from {theWeekOf(carry.fromWeek)}&rsquo;s report</div> : null;
  return (
    <Section title="Manpower" tag="Internal only">
      <div className="ew-table-scroll">
        <table className="wr-table ew-cards">
          <thead><tr><th>Week Of</th><th>Headcount</th><th>Notes</th></tr></thead>
          <tbody>
            {manpowerRows(report).map((row, i) => (
              <tr key={`${row.label}-${i}`}>
                <td data-label="Week of" className="wr-label">{row.label}{row.added && from}</td>
                <td data-label="Headcount">{row.headcount || '—'}{row.headcountCarried && !row.added && from}</td>
                <td data-label="Notes">
                  {row.trackerNote && <div className="wr-muted">{row.trackerNote}</div>}
                  {row.typedNote && <div>{row.typedNote}{!row.added && from}</div>}
                  {!row.trackerNote && !row.typedNote && <span className="wr-muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="wr-muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
        {MANPOWER_FOOTNOTE}
      </p>
    </Section>
  );
}

function EquipmentSection({ report }: { report: InternalWeeklyReport }) {
  const carry = report.carryForward;
  const typed = typedEquipment(carry);
  return (
    <Section title="Equipment" tag="Internal only">
      {report.equipment.length === 0
        ? <p className="wr-muted" style={{ margin: 0 }}>No equipment on the job this week. Owned items such as conexes can be added in Word.</p>
        : (
          <ul className="wr-list">
            {report.equipment.map((e) => (
              <li key={e.id}>
                {e.quantity && e.quantity > 1 ? `${e.quantity} × ` : ''}{e.name}
                <span className="wr-muted">
                  {e.status ? ` · ${e.status}` : ''}
                  {e.onSiteFrom ? ` · since ${longDate(e.onSiteFrom)}` : ''}
                  {e.endDate ? ` · through ${longDate(e.endDate)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      {typed.length > 0 && carry && <WrittenInWord lines={typed} fromWeek={carry.fromWeek} />}
    </Section>
  );
}

function DayView({ day, thumbs, settled }: {
  day: WeeklyReportDay;
  thumbs: Record<string, string>;
  settled: (u: string) => boolean;
}) {
  const log = day.log;
  const hasPhotos = day.photoPosts.some((p) => p.photos.length > 0);
  const work = log?.work.filter((x) => (x.area ?? '').trim() || (x.narrative ?? '').trim()) ?? [];
  const qty = log?.quantities.map(quantityLine).filter(Boolean) ?? [];
  return (
    <div className="wr-day">
      <div className="wr-day-hd">
        <span>{dayHeading(day.date)}</span>
        {log
          ? <Badge label={log.status === 'submitted' ? 'Submitted' : 'Draft log · not exported'} tone={log.status === 'submitted' ? 'success' : 'warning'} />
          : <Badge label="Photos only" tone="neutral" />}
        {log && <Link to={`?tab=daily&log=${log.id}`}>Open daily log →</Link>}
      </div>
      <div className="wr-day-body">
        {log && (
          <>
            {weatherLine(log.weather) && <p><strong>Weather:</strong> {weatherLine(log.weather)}</p>}
            {/* "Submitted by" only once it is, as the Daily Logs tab says it. */}
            {log.author && <p><strong>{log.status === 'submitted' ? 'Submitted by' : 'Started by'}:</strong> {log.author}</p>}
            {log.crews.length > 0 && (() => {
              const t = crewTotals(log);
              return (
                <>
                  <div className="wr-day-label">Crews · {t.workers} workers · {t.manhours.toLocaleString('en-US', { maximumFractionDigits: 1 })} man-hours</div>
                  <div className="ew-table-scroll" style={{ marginTop: 4 }}>
                    <table className="wr-table ew-cards">
                      <thead><tr><th>Company</th><th>Trade</th><th>Workers</th><th>Hours</th></tr></thead>
                      <tbody>
                        {log.crews.map((c, i) => (
                          <tr key={i}>
                            <td data-label="Company">{c.company ?? '—'}</td>
                            <td data-label="Trade">{c.trade ?? '—'}</td>
                            <td data-label="Workers">{c.workers ?? '—'}</td>
                            <td data-label="Hours">{c.hours ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
            {work.length > 0 && (
              <>
                <div className="wr-day-label">Work performed</div>
                <ul>{work.map((x, i) => <li key={i}>{[x.area, x.narrative].filter(Boolean).join(': ')}</li>)}</ul>
              </>
            )}
            {qty.length > 0 && (
              <>
                <div className="wr-day-label">Quantities installed</div>
                <ul>{qty.map((q, i) => <li key={i}>{q}</li>)}</ul>
              </>
            )}
            {log.workSummary && <p style={{ marginTop: 8 }}><strong>Summary:</strong> {log.workSummary}</p>}
            {log.delays && <p><strong>Delays:</strong> {log.delays}</p>}
            {log.safetyNotes && <p><strong>Safety:</strong> {log.safetyNotes}</p>}
          </>
        )}
        {!log && <p className="wr-muted">No daily log for this day.</p>}
        {hasPhotos && (
          <>
            <div className="wr-day-label">Photos</div>
            <ReportPhotoGrid day={day} thumbs={thumbs} settled={settled} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Client export: choose the days ──────────────────────────────────────────

function ClientDaysModal({ report, onClose, onExport }: {
  report: InternalWeeklyReport;
  onClose: () => void;
  onExport: (days: string[]) => void;
}) {
  // Nothing ticked by default (#579): a client sees a daily log only when the
  // PM chooses it.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const toggle = (d: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(d)) next.delete(d); else next.add(d);
    return next;
  });
  const setMany = (ds: string[], on: boolean) => setPicked((prev) => {
    const next = new Set(prev);
    for (const d of ds) if (on) next.add(d); else next.delete(d);
    return next;
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Export client report"
      maxWidth={560}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onExport([...picked].sort())}>
            Export client report{picked.size ? ` with ${picked.size} day${picked.size === 1 ? '' : 's'}` : ''}
          </Button>
        </>
      )}
    >
      <p style={{ marginTop: 0 }}>
        Choose the days whose daily logs and photos go in the client copy. None are included unless you tick them.
        Manpower and equipment are never in the client copy, and draft logs aren&rsquo;t exported.
      </p>
      {daysByWeek(report).map(({ weekStart, days }) => {
        const choosable = days.filter(dayHasExportContent).map((d) => d.date);
        const allOn = choosable.length > 0 && choosable.every((d) => picked.has(d));
        return (
          <div key={weekStart}>
            <div className="wr-pick-week">
              <span>{weekOf(weekStart)}</span>
              {choosable.length > 0 && (
                <button type="button" className="ew-btn-link" onClick={() => setMany(choosable, !allOn)}>
                  {allOn ? 'Clear week' : 'Select week'}
                </button>
              )}
            </div>
            {days.length === 0 && <p className="wr-muted" style={{ margin: '4px 0' }}>No daily logs or photos this week.</p>}
            {days.map((d) => {
              const can = dayHasExportContent(d);
              const n = photoCount(d);
              const what = [
                d.log ? (d.log.status === 'submitted' ? 'Daily log' : 'Draft log, not exported') : null,
                n ? `${n} photo${n === 1 ? '' : 's'}` : null,
              ].filter(Boolean).join(' · ');
              return (
                <label key={d.date} className="wr-pick" style={can ? undefined : { opacity: 0.55 }}>
                  <input type="checkbox" disabled={!can} checked={picked.has(d.date)} onChange={() => toggle(d.date)} />
                  <span>{dayHeading(d.date)}<br /><span className="wr-muted">{what}</span></span>
                </label>
              );
            })}
          </div>
        );
      })}
    </Modal>
  );
}
